import React, { useState, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import {
  CloudUpload,
  Delete,
  Visibility,
  CheckCircle,
  Error as ErrorIcon,
  Description
} from '@mui/icons-material'
import { useDropzone } from 'react-dropzone'
import { useSnackbar } from 'notistack'
import documentService from '../../../services/documentService'

const DocumentUpload = ({ fileId, onUploadComplete }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [openMetadata, setOpenMetadata] = useState(false)
  const [currentFile, setCurrentFile] = useState(null)
  const [metadata, setMetadata] = useState({
    documentType: '',
    description: '',
    category: 'MEDICAL_RECORD'
  })

  const documentTypes = [
    'DISCHARGE_SUMMARY',
    'OPERATION_NOTES',
    'CONSULTATION_NOTES',
    'LAB_REPORTS',
    'RADIOLOGY_REPORTS',
    'NURSING_NOTES',
    'MEDICATION_CHART',
    'CONSENT_FORMS',
    'INSURANCE_DOCUMENTS',
    'OTHER'
  ]

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      status: 'pending',
      progress: 0
    }))
    setSelectedFiles(prev => [...prev, ...newFiles])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxSize: 10 * 1024 * 1024 // 10MB
  })

  const removeFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId))
  }

  const handleUploadWithMetadata = (file) => {
    setCurrentFile(file)
    setOpenMetadata(true)
  }

  const uploadFile = async (fileData, fileMetadata) => {
    try {
      const formData = new FormData()
      formData.append('document', fileData.file)
      formData.append('documentType', fileMetadata.documentType)
      formData.append('description', fileMetadata.description)
      formData.append('category', fileMetadata.category)

      const response = await documentService.uploadDocument(fileId, formData)
      
      if (response.data.success) {
        setSelectedFiles(prev => 
          prev.map(f => 
            f.id === fileData.id 
              ? { ...f, status: 'completed', uploadedData: response.data.data }
              : f
          )
        )
        enqueueSnackbar('Document uploaded successfully', { variant: 'success' })
        if (onUploadComplete) onUploadComplete()
      }
    } catch (error) {
      setSelectedFiles(prev => 
        prev.map(f => 
          f.id === fileData.id 
            ? { ...f, status: 'error', error: error.message }
            : f
        )
      )
      enqueueSnackbar('Failed to upload document', { variant: 'error' })
    }
  }

  const handleMetadataSubmit = () => {
    if (!metadata.documentType) {
      enqueueSnackbar('Please select document type', { variant: 'error' })
      return
    }

    uploadFile(currentFile, metadata)
    setOpenMetadata(false)
    setMetadata({ documentType: '', description: '', category: 'MEDICAL_RECORD' })
    setCurrentFile(null)
  }

  const uploadAllFiles = async () => {
    setUploading(true)
    const pendingFiles = selectedFiles.filter(f => f.status === 'pending')
    
    for (const file of pendingFiles) {
      await uploadFile(file, {
        documentType: 'OTHER',
        description: file.file.name,
        category: 'MEDICAL_RECORD'
      })
    }
    
    setUploading(false)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />
      case 'error':
        return <ErrorIcon color="error" />
      default:
        return <Description />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'error':
        return 'error'
      default:
        return 'default'
    }
  }

  return (
    <Box>
      {/* Upload Area */}
      <Paper
        {...getRootProps()}
        sx={{
          p: 6,
          textAlign: 'center',
          border: '3px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          bgcolor: isDragActive ? 'primary.50' : 'grey.50',
          cursor: 'pointer',
          mb: 4,
          borderRadius: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'primary.50',
            transform: 'translateY(-2px)',
            boxShadow: 3
          }
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 3 }} />
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {isDragActive ? 'Drop files here' : 'Upload Documents'}
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          Drag & drop files here or click to browse
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ 
          bgcolor: 'white', 
          p: 1, 
          borderRadius: 1, 
          display: 'inline-block',
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          Supported formats: PDF, DOC, DOCX, JPG, PNG • Maximum size: 10MB per file
        </Typography>
      </Paper>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <Paper sx={{ mb: 4, borderRadius: 2 }}>
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="h6" fontWeight={600}>
              Selected Files ({selectedFiles.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setSelectedFiles([])}
                disabled={uploading}
                size="large"
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                onClick={uploadAllFiles}
                disabled={uploading || selectedFiles.every(f => f.status !== 'pending')}
                startIcon={<CloudUpload />}
                size="large"
              >
                Upload All Files
              </Button>
            </Box>
          </Box>
          
          {uploading && <LinearProgress />}
          
          <List>
            {selectedFiles.map((fileData) => (
              <ListItem key={fileData.id} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(fileData.status)}
                      <Typography variant="body1">
                        {fileData.file.name}
                      </Typography>
                      <Chip
                        label={fileData.status}
                        size="small"
                        color={getStatusColor(fileData.status)}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption">
                        Size: {(fileData.file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      {fileData.error && (
                        <Typography variant="caption" color="error" display="block">
                          Error: {fileData.error}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {fileData.status === 'pending' && (
                      <Button
                        size="small"
                        onClick={() => handleUploadWithMetadata(fileData)}
                      >
                        Add Details
                      </Button>
                    )}
                    {fileData.status === 'completed' && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          // Handle view document
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => removeFile(fileData.id)}
                      disabled={uploading}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Metadata Dialog */}
      <Dialog open={openMetadata} onClose={() => setOpenMetadata(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Document Details</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Document Type *</InputLabel>
              <Select
                value={metadata.documentType}
                label="Document Type *"
                onChange={(e) => setMetadata(prev => ({ ...prev, documentType: e.target.value }))}
              >
                {documentTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={metadata.category}
                label="Category"
                onChange={(e) => setMetadata(prev => ({ ...prev, category: e.target.value }))}
              >
                <MenuItem value="MEDICAL_RECORD">Medical Record</MenuItem>
                <MenuItem value="ADMINISTRATIVE">Administrative</MenuItem>
                <MenuItem value="INSURANCE">Insurance</MenuItem>
                <MenuItem value="LEGAL">Legal</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={metadata.description}
              onChange={(e) => setMetadata(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter document description..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMetadata(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleMetadataSubmit}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DocumentUpload